import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Stack, Typography, Box, Chip, Alert, LinearProgress, Divider, Stepper, Step, StepLabel } from "@mui/material"
import { CheckCircle, Error, Pending, Receipt, Timeline, AccessTime, Person } from "@mui/icons-material"
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import PaperDraggable from "../../../../../../components/PaperDraggable"
import { Controller, useForm } from "react-hook-form";
import { formatarInputDecimal, permitirApenasNumeros } from "../../../../../../helpers/masks";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { IEmitirNotaMutation } from "../../../../../../types/mutations/comercial/emitir-nota-fiscal";
import { IVerificarStatusNfe } from "../../../../../../types/api/estoque/verificar-status-nfe";
import axiosClient from "../../../../../../lib/axios";
import useImprimirArquivo from "../../../../../../lib/useImprimirArquivo";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import moment from "moment";

export default function DialogEmitirNfe({
    open,
    handleClose,
    transportadorId,
    expedicaoId
}: DialogEmitirNfeProps) {

    const queryClient = useQueryClient();
    const [showStatus, setShowStatus] = useState(false);
    const [mockStep, setMockStep] = useState(0);
    const imprimirArquivo = useImprimirArquivo();

    const methods = useForm<TEmitirNotaFiscal>({
        defaultValues: {
            qtd_volumes: "",
            peso_liquido: "",
            peso_bruto: "",
        }
    })
    const { handleSubmit, control, setValue } = methods;

    const { data: statusData, isLoading: isLoadingStatus } = useQuery({
        queryKey: [`verificar-status-nfe/${expedicaoId}`, expedicaoId],
        queryFn: () => {
            if (process.env.REACT_APP_ENABLE_MOCKS === 'true') {
                return new Promise<{ data: IVerificarStatusNfe }>((resolve) => {
                    setTimeout(() => {
                        if (mockStep === 0) {
                            resolve({ data: MOCK_STATUS_DATA });
                        } else if (mockStep === 1) {
                            resolve({ data: MOCK_SUCCESS_DATA });
                        } else {
                            resolve({ data: MOCK_ERROR_DATA });
                        }
                    }, 1000);
                });
            }
            return axiosClient.get<IVerificarStatusNfe>(`/estoque/expedicao/verificar-status-nfe/${expedicaoId}`, {
                params: {
                    origem_emissao: "ressuprimento"
                }
            });
        },
        keepPreviousData: false,
        refetchOnWindowFocus: true,
        cacheTime: 0,
        enabled: open,
        refetchInterval: showStatus ? 3000 : false,
        onSuccess: (data) => {
            if (!data.data.status_nf) {
                setValue('qtd_volumes', data.data.volume.toString());
                setValue('peso_bruto', data.data.peso_bruto.toString().replace('.', ','));
                setValue('peso_liquido', data.data.peso_liquido.toString().replace('.', ','));
                return;
            }
            if (data.data.possui_nota_fiscal && data.data.status_nf === "Nota fiscal emitida") {
                setShowStatus(true);
            }
            if (data.data.status_nf === "Erro na emissão") {
                setShowStatus(false);
            }
        },
    });

    const { mutate: emitir, isLoading } = useMutation({
        mutationKey: 'emitir-nota-fiscal',
        mutationFn: (inputs: IEmitirNotaMutation) => {
            if (process.env.REACT_APP_ENABLE_MOCKS === 'true') {
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve({ data: { message: 'Emissão iniciada' } });

                        setTimeout(() => {
                            setMockStep(1);
                        }, 3000);
                    }, 1000);
                });
            }
            return axiosClient.post(`comercial/pedidos/emitir-nota-fiscal`, inputs);
        },
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao emitir nota fiscal!', { variant: 'error' });
            setShowStatus(false);
        },
        onSuccess: () => {
            enqueueSnackbar('Iniciando emissão da nota fiscal...', { variant: 'info' });
            setShowStatus(true);
        }
    })

    const { mutate: tentarNovamente, isLoading: isLoadingRetry } = useMutation({
        mutationKey: 'tentar-novamente-nfe',
        mutationFn: (expedicaoId: number) => {
            if (process.env.REACT_APP_ENABLE_MOCKS === 'true') {
                return new Promise((resolve) => {
                    setTimeout(() => {
                        setMockStep(1);
                        resolve({ data: { message: 'Tentativa iniciada' } });
                    }, 1000);
                });
            }
            return axiosClient.put(`/comercial/pedidos/emitir-nota-fiscal/tentar-novamente/${expedicaoId}`);
        },
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao tentar novamente!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar('Tentando emitir nota fiscal novamente...', { variant: 'info' });
            queryClient.invalidateQueries(`verificar-status-nfe/${expedicaoId}`);
            setShowStatus(true);
        }
    });

    const onSubmit = (data: TEmitirNotaFiscal) => {
        const converterDecimal = (valor: string): number | null => {
            if (!valor || valor.trim() === '') return null;
            const numero = Number(valor.replace(',', '.'));
            return isNaN(numero) ? null : numero;
        };

        const params: IEmitirNotaMutation = {
            expedicao_id: expedicaoId,
            forma_entrega: "Pela Cooperativa",
            transportador_id: transportadorId,
            peso_bruto: converterDecimal(data.peso_bruto),
            peso_liquido: converterDecimal(data.peso_liquido),
            qtd_volumes: Number(data.qtd_volumes) || null,
            origem_emissao: "ressuprimento"
        }
        emitir(params)
    }

    const handleCloseDialog = () => {
        setShowStatus(false);
        setMockStep(0);
        handleClose();
    }

    const handleImprimirDanfe = () => {
        imprimirArquivo(`comercial/pedidos/imprimir-danfe/${expedicaoId}`);
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "Nota fiscal emitida":
                return <CheckCircle color="success" />;
            case "Erro na emissão":
                return <Error color="error" />;
            case "Emissão em andamento":
                return <Pending color="warning" />;
            default:
                return <Timeline color="info" />;
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Nota fiscal emitida":
                return "success";
            case "Erro na emissão":
                return "error";
            case "Emissão em andamento":
                return "warning";
            default:
                return "default";
        }
    }

    const formatarData = (dataString: string) => {
        if (!dataString || dataString.trim() === '' || dataString === "0000-00-00") {
            return "-";
        }
        if (dataString.includes('/') && dataString.includes(':')) {
            return dataString;
        }
        const data = moment(dataString);

        if (!data.isValid()) {
            return "-";
        }
        return data.format('DD/MM/YYYY');
    }

    const shouldShowForm = !showStatus && !isLoadingStatus &&
        (!statusData?.data?.possui_nota_fiscal && (statusData?.data?.status_nf !== "Nota fiscal emitida" && statusData?.data?.status_nf !== "Erro na emissão"));


    const isConsultaNotaEmitida = statusData?.data?.possui_nota_fiscal &&
        statusData?.data?.status_nf === "Nota fiscal emitida";

    return (
        <Dialog
            open={open}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            scroll="body"
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={statusData?.data?.status_nf === "Emissão em andamento"}
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {isConsultaNotaEmitida ? "Nota Fiscal Emitida" : "Emitir nota fiscal"}
            </DialogTitle>

            <DialogContent>
                {/* Botões de teste apenas se não for consulta de nota já emitida */}
                {(process.env.REACT_APP_ENABLE_MOCKS === 'true' && showStatus) && (
                    <Box mb={2} p={2} bgcolor="grey.100" borderRadius={1}>
                        <Typography variant="subtitle2" mb={1}>🧪 Teste:</Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Para testar os diferentes estados da emissão da nota fiscal, use os botões abaixo.
                            Cada botão simula um cenário diferente com delay de 1 segundo para simular o tempo de resposta da API:
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setMockStep(0)}
                            >
                                Em Andamento
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setMockStep(1)}
                            >
                                Sucesso
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setMockStep(2)}
                            >
                                Erro
                            </Button>
                        </Stack>
                    </Box>
                )}

                {/* Loading inicial */}
                {isLoadingStatus && !showStatus && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                        <Typography variant="body2" ml={2}>
                            Verificando status da nota fiscal...
                        </Typography>
                    </Box>
                )}

                {/* Formulário - apenas se deve mostrar formulário */}
                {shouldShowForm && (
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                control={control}
                                name="qtd_volumes"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { invalid, error } }) => (
                                    <TextField
                                        required
                                        fullWidth
                                        type="number"
                                        label="Quantidade de volumes"
                                        value={field.value}
                                        onChange={(e) => field.onChange(
                                            permitirApenasNumeros(e.target.value))
                                        }
                                        error={invalid}
                                        helperText={error ? "Campo obrigatório" : ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                control={control}
                                name="peso_liquido"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { invalid, error } }) => (
                                    <TextField
                                        required
                                        fullWidth
                                        type="text"
                                        label="Peso líquido (kg)"
                                        value={field.value}
                                        onChange={(e) => {
                                            const valor = formatarInputDecimal(e.target.value, 3);
                                            field.onChange(valor);
                                        }}
                                        error={invalid}
                                        helperText={error ? "Campo obrigatório" : ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                            <Controller
                                control={control}
                                name="peso_bruto"
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { invalid, error } }) => (
                                    <TextField
                                        required
                                        fullWidth
                                        label="Peso bruto (kg)"
                                        type="text"
                                        value={field.value}
                                        onChange={(e) => {
                                            const valor = formatarInputDecimal(e.target.value, 3);
                                            field.onChange(valor);
                                        }}
                                        error={invalid}
                                        helperText={error ? "Campo obrigatório" : ""}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Status display - para emissão em andamento ou nota já emitida */}
                {(showStatus || isConsultaNotaEmitida || !shouldShowForm) && (
                    <Box mt={2}>
                        <Stack spacing={3}>
                            {/* Status atual */}
                            <Box>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    {getStatusIcon(statusData?.data?.status_nf || "")}
                                    <Typography variant="h6" color="text.primary">
                                        {isConsultaNotaEmitida ? "Status da Nota Fiscal" : "Status da Emissão"}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={2} alignItems="center">
                                    {isLoadingStatus && (
                                        <CircularProgress size={20} />
                                    )}
                                    <Chip
                                        label={statusData?.data?.status_nf || "Verificando..."}
                                        color={getStatusColor(statusData?.data?.status_nf || "")}
                                        variant="filled"
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            minHeight: '32px'
                                        }}
                                    />
                                </Stack>

                                {/* Progress bar para emissão em andamento */}
                                {statusData?.data?.status_nf === "Emissão em andamento" && (
                                    <Box mt={2}>
                                        <LinearProgress
                                            variant="indeterminate"
                                            color="warning"
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Divider />

                            {/* Informações da NFE */}
                            {statusData?.data?.numero_nf && (
                                <Box>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                        <Receipt color="primary" />
                                        <Typography variant="h6" color="text.primary">
                                            Informações da Nota Fiscal
                                        </Typography>
                                    </Stack>

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12 }}>
                                            <Box p={2} bgcolor="grey.50" borderRadius={1}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Chave de Acesso
                                                </Typography>
                                                <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                                                    {statusData.data.chave_acesso}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Box p={2} bgcolor="grey.50" borderRadius={1}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Número / Série
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {statusData.data.numero_nf} / {statusData.data.serie_nf}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Box p={2} bgcolor="grey.50" borderRadius={1}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Data de Emissão
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {formatarData(statusData.data.data_emissao_nf)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Divider />
                                </Box>
                            )}

                            {/* Erro na emissão */}
                            {statusData?.data?.status_nf === "Erro na emissão" && (
                                <Box>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                        <Error color="error" />
                                        <Typography variant="h6" color="error">
                                            Erro na Emissão
                                        </Typography>
                                    </Stack>
                                    <Alert severity="error" variant="outlined">
                                        <Typography variant="body2">
                                            {statusData.data.erro_nota_fiscal || "Erro na emissão da nota fiscal"}
                                        </Typography>
                                    </Alert>

                                    <Divider />
                                </Box>
                            )}

                            {/* Histórico de status com Stepper */}
                            {statusData?.data?.historico_status && statusData.data.historico_status.length > 0 && (
                                <Box>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                                        <Timeline color="primary" />
                                        <Typography variant="h6" color="text.primary">
                                            Histórico de Status
                                        </Typography>
                                    </Stack>

                                    <Stepper orientation="vertical" activeStep={statusData.data.historico_status.length - 1}>
                                        {statusData.data.historico_status.map((status, index) => {
                                            const statusText = typeof status === 'string'
                                                ? status
                                                : status.error || 'Status não disponível';

                                            const statusDate = typeof status === 'object' && status.data
                                                ? status.data
                                                : null;

                                            return (
                                                <Step key={index}>
                                                    <StepLabel
                                                        optional={
                                                            <>
                                                                {index === statusData.data.historico_status.length - 1 ? (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {statusData.data.status_nf === "Nota fiscal emitida" ? "Concluído" :
                                                                            statusData.data.status_nf === "Erro na emissão" ? "Erro" : "Em andamento"}
                                                                    </Typography>
                                                                ) : null}
                                                                {statusDate && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        {formatarData(statusDate)}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        }
                                                    >
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {statusText}
                                                        </Typography>
                                                    </StepLabel>
                                                </Step>
                                            );
                                        })}
                                    </Stepper>
                                </Box>
                            )}

                            <Divider />

                            {/* Informações de verificação */}
                            {statusData?.data?.verificado_em && (
                                <Box>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                                        <AccessTime color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            Verificado em: {formatarData(statusData.data.verificado_em)}
                                        </Typography>
                                    </Stack>
                                    {statusData.data.verificado_por && (
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Person color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Por: {statusData.data.verificado_por}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            )}
                        </Stack>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleCloseDialog}
                    disabled={isLoading || isLoadingRetry || (showStatus && statusData?.data?.status_nf === "Emissão em andamento")}
                >
                    Fechar
                </Button>

                {shouldShowForm && (
                    <Button
                        autoFocus
                        variant="contained"
                        disabled={isLoading}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Confirmar
                    </Button>
                )}

                {statusData?.data?.status_nf === "Nota fiscal emitida" && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleImprimirDanfe}
                        startIcon={<LocalPrintshopOutlinedIcon fontSize="small" />}
                    >
                        Imprimir DANFE
                    </Button>
                )}

                {((showStatus && !isConsultaNotaEmitida) || statusData?.data?.status_nf === "Erro na emissão") && (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isLoadingRetry}
                        onClick={() => tentarNovamente(expedicaoId)}
                    >
                        {isLoadingRetry && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        Tentar Novamente
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

interface DialogEmitirNfeProps {
    open: boolean,
    handleClose: () => void,
    transportadorId: number,
    expedicaoId: number,
}

type TEmitirNotaFiscal = {
    qtd_volumes: string,
    peso_liquido: string,
    peso_bruto: string,
}


// Mockup para desenvolvimento
const MOCK_STATUS_DATA: IVerificarStatusNfe = {
    expedicao_id: 1,
    pedido_cs9: 12345,
    status_expedicao: "Em andamento",
    status_nf: "Emissão em andamento",
    numero_nf: "",
    serie_nf: "",
    data_emissao_nf: "",
    chave_acesso: "",
    possui_nota_fiscal: false,
    erro_nota_fiscal: null,
    historico_status: ["Iniciando emissão", "Validando dados", "Enviando para SEFAZ"],
    verificado_em: new Date().toISOString(),
    verificado_por: "Sistema",
    volume: 5,
    peso_bruto: 50,
    peso_liquido: 45
};

const MOCK_SUCCESS_DATA: IVerificarStatusNfe = {
    expedicao_id: 1,
    pedido_cs9: 12345,
    status_expedicao: "Concluída",
    status_nf: "Nota fiscal emitida",
    numero_nf: "000123456",
    serie_nf: "1",
    data_emissao_nf: new Date().toISOString(),
    chave_acesso: "12345678901234567890123456789012345678901234",
    possui_nota_fiscal: true,
    erro_nota_fiscal: null,
    historico_status: ["Iniciando emissão", "Validando dados", "Enviando para SEFAZ", "Nota fiscal emitida"],
    verificado_em: new Date().toISOString(),
    verificado_por: "Sistema",
    volume: 10,
    peso_bruto: 100,
    peso_liquido: 90
};

const MOCK_ERROR_DATA: IVerificarStatusNfe = {
    expedicao_id: 1,
    pedido_cs9: 12345,
    status_expedicao: "Erro",
    status_nf: "Erro na emissão",
    numero_nf: "",
    serie_nf: "",
    data_emissao_nf: "",
    chave_acesso: "",
    possui_nota_fiscal: false,
    erro_nota_fiscal: "Erro de conexão com SEFAZ. Tente novamente.",
    historico_status: ["Iniciando emissão", "Validando dados", "Erro na conexão"],
    verificado_em: new Date().toISOString(),
    verificado_por: "Sistema",
    volume: 0,
    peso_bruto: 0,
    peso_liquido: 0
};